/**
 * Extractors — turn raw text into the structured `ParsedDocument` shape
 * defined in `types.ts`. Pure functions, no I/O, no dependencies.
 *
 * Sections are detected by heading lines that match a curated vocabulary
 * of resume and JD section names (case-insensitive). Bullets are detected
 * by leading bullet characters (-, *, •, · or numeric "1.") or by lines
 * that look like short imperative starters in an experience section.
 */

import type { Bullet, JobDescription, ParsedDocument, Resume } from "./types.js";

// ---------------------------------------------------------------------------
// Heading taxonomies
// ---------------------------------------------------------------------------
const RESUME_HEADINGS = [
  { canonical: "Summary", patterns: ["summary", "profile", "objective", "about"] },
  { canonical: "Skills", patterns: ["skills", "technical skills", "core competencies", "expertise", "technologies"] },
  { canonical: "Experience", patterns: ["experience", "professional experience", "work experience", "employment", "work history", "career"] },
  { canonical: "Education", patterns: ["education", "academic background", "academics"] },
  { canonical: "Projects", patterns: ["projects", "selected projects", "personal projects"] },
  { canonical: "Publications", patterns: ["publications", "papers", "research", "selected publications"] },
  { canonical: "Certifications", patterns: ["certifications", "certificates", "licenses"] },
  { canonical: "Awards", patterns: ["awards", "honors", "recognition"] },
  { canonical: "Languages", patterns: ["languages"] },
  { canonical: "Interests", patterns: ["interests", "hobbies"] },
];

const JD_HEADINGS = [
  { canonical: "About", patterns: ["about", "about the role", "about us", "about the company", "the role", "overview"] },
  { canonical: "Responsibilities", patterns: ["responsibilities", "what you'll do", "what you will do", "your role", "the job", "day to day", "day-to-day"] },
  { canonical: "Requirements", patterns: ["requirements", "required", "must have", "must-have", "qualifications", "what you'll bring", "what you bring", "what we're looking for", "who you are"] },
  { canonical: "Preferred", patterns: ["preferred", "nice to have", "nice-to-have", "bonus", "plus", "preferred qualifications", "nice to haves"] },
  { canonical: "Benefits", patterns: ["benefits", "what we offer", "perks", "compensation"] },
  { canonical: "About Company", patterns: ["about the company", "our company", "who we are"] },
  { canonical: "Application", patterns: ["how to apply", "apply", "application process"] },
];

interface HeadingMatch {
  canonical: string;
  matchLength: number;
}

function matchHeading(
  line: string,
  taxonomy: Array<{ canonical: string; patterns: string[] }>,
): HeadingMatch | null {
  const trimmed = line.trim();
  if (trimmed.length === 0 || trimmed.length > 80) return null;

  // Heading lines are typically short, often UPPERCASE or Title Case, and
  // often followed by a colon or nothing. We test normalized lowercase.
  const lower = trimmed
    .toLowerCase()
    .replace(/[:.].*$/, "") // drop trailing colons and explanation
    .trim();

  for (const entry of taxonomy) {
    for (const pattern of entry.patterns) {
      if (lower === pattern) return { canonical: entry.canonical, matchLength: pattern.length };
      // heading followed by a dash / divider: "experience ----"
      if (lower.startsWith(pattern) && lower.length - pattern.length <= 6) {
        return { canonical: entry.canonical, matchLength: pattern.length };
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Section parser
// ---------------------------------------------------------------------------
interface ParseOptions {
  kind: "resume" | "jd";
}

function parseSections(rawText: string, opts: ParseOptions): Record<string, string> {
  const taxonomy = opts.kind === "resume" ? RESUME_HEADINGS : JD_HEADINGS;
  const lines = rawText.split(/\r?\n/);
  const sections: Record<string, string[]> = { Misc: [] };
  let current = "Misc";

  for (const line of lines) {
    const match = matchHeading(line, taxonomy);
    if (match) {
      current = match.canonical;
      if (!sections[current]) sections[current] = [];
      continue;
    }
    if (line.trim().length === 0) {
      sections[current]?.push("");
      continue;
    }
    sections[current]?.push(line);
  }

  // Collapse to strings, drop empty sections, trim excess whitespace
  const out: Record<string, string> = {};
  for (const [name, linesArr] of Object.entries(sections)) {
    const joined = linesArr.join("\n").trim();
    if (joined.length > 0) out[name] = joined;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Bullet extractor
// ---------------------------------------------------------------------------
const BULLET_RE = /^\s*[-•·*●◦▪▫►▶‣⁃]\s+(.+)$/;
const NUMBERED_RE = /^\s*\d+[.)]\s+(.+)$/;

function extractBulletsFromSection(section: string, sectionName: string): Bullet[] {
  const bullets: Bullet[] = [];
  const lines = section.split(/\r?\n/);
  let index = 0;
  for (const line of lines) {
    const b = line.match(BULLET_RE);
    const n = line.match(NUMBERED_RE);
    const text = b?.[1] ?? n?.[1];
    if (text && text.trim().length > 0) {
      bullets.push({
        section: sectionName,
        index,
        text: text.trim(),
      });
      index++;
    }
  }
  return bullets;
}

function extractAllBullets(sections: Record<string, string>): Bullet[] {
  const bullets: Bullet[] = [];
  for (const [name, content] of Object.entries(sections)) {
    bullets.push(...extractBulletsFromSection(content, name));
  }
  return bullets;
}

// ---------------------------------------------------------------------------
// Entity extraction — simple regex patterns for the Day 2 baseline.
// Day 3+ replaces with NER if quality demands it.
// ---------------------------------------------------------------------------
const YEARS_RE = /\b(19|20)\d{2}\b/g;
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const PHONE_RE = /(?:\+?\d{1,3}[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g;
const URL_RE = /\bhttps?:\/\/[^\s<>"']+/g;

function extractEntities(rawText: string): Record<string, string[]> {
  return {
    years: [...rawText.matchAll(YEARS_RE)].map((m) => m[0]),
    emails: [...rawText.matchAll(EMAIL_RE)].map((m) => m[0]),
    phones: [...rawText.matchAll(PHONE_RE)].map((m) => m[0]),
    urls: [...rawText.matchAll(URL_RE)].map((m) => m[0]),
  };
}

// ---------------------------------------------------------------------------
// Public: parse raw text into a ParsedDocument
// ---------------------------------------------------------------------------
export function parseResume(rawText: string): Resume {
  const sections = parseSections(rawText, { kind: "resume" });
  const bullets = extractAllBullets(sections);
  const entities = extractEntities(rawText);
  // Best-effort display name: first non-empty line that looks like a name
  // (title case, 2-4 words, not an email, not all-caps URL).
  const displayName = extractDisplayName(rawText);
  return {
    rawText,
    sections,
    bullets,
    entities,
    displayName,
  };
}

export function parseJobDescription(rawText: string): JobDescription {
  const sections = parseSections(rawText, { kind: "jd" });
  const bullets = extractAllBullets(sections);
  const entities = extractEntities(rawText);
  const title = extractJobTitle(rawText);
  return {
    rawText,
    sections,
    bullets,
    entities,
    title,
  };
}

/** Used by the section scorer — returns a document's sections as pairs. */
export function sectionPairs(doc: ParsedDocument): Array<[string, string]> {
  return Object.entries(doc.sections);
}

// ---------------------------------------------------------------------------
// Name / title extractors — best effort for Day 2. Not load-bearing.
// ---------------------------------------------------------------------------
function extractDisplayName(rawText: string): string | null {
  const lines = rawText.split(/\r?\n/);
  for (const line of lines.slice(0, 6)) {
    const t = line.trim();
    if (t.length === 0) continue;
    if (t.includes("@")) continue;
    if (/^https?:\/\//i.test(t)) continue;
    const words = t.split(/\s+/);
    if (words.length < 2 || words.length > 5) continue;
    // Each word should start uppercase (name-ish), optionally followed by
    // post-nominal letters like "PhD" or "MD"
    const allNameShaped = words.every((w) =>
      /^[A-Z][a-zA-Z.,'-]*$/.test(w),
    );
    if (allNameShaped) return t;
  }
  return null;
}

function extractJobTitle(rawText: string): string | null {
  const lines = rawText.split(/\r?\n/);
  for (const line of lines.slice(0, 8)) {
    const t = line.trim();
    if (t.length === 0) continue;
    if (t.length > 90) continue;
    // Title line: contains a job role word and isn't all caps
    if (/engineer|scientist|manager|designer|developer|analyst|architect|lead|director|officer|researcher|specialist|consultant|president|nurse|practitioner|attorney|lawyer|recruiter|marketer|coordinator|associate|partner|counsel|accountant/i.test(t)) {
      return t;
    }
  }
  return null;
}
